/** @format */

import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import withStyles from '@material-ui/core/styles/withStyles';
import { Auth } from '../../modules/auth/Auth';
import SimCardInterface from '../../interfaces/platform/SimCardInterface';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { history } from '../../core/history';
import GridContainer from '../../components/Grid/GridContainer';
import Card from '../../components/Card/Card';
import CardHeader from '../../components/Heading/CardHeaderExt';
import SimCard from '@material-ui/icons/SimCard';
import CardBody from '../../components/Card/CardBody';
import GridItem from '../../components/Grid/GridItem';
import SimCardView from './SimCardViewCommon';
import { SimCardOperationHelper } from '../../helpers/SimCardOperationHelper';
import { roles, UserRoleHelper } from '../../helpers/UserRoleHelper';
import FeaturesActiveList from '../Features/FeaturesActiveList';
import FlashOn from '@material-ui/icons/FlashOn';
import FeaturesNotActiveList from '../Features/FeaturesNotActiveList';
import { ProductActionField, ProductActions } from '../../modules/simcard/ProductReducer';
import { ProductOplView } from './component/ProductOplView';
import MnoMessageList from '../MnoMessage/MnoMessageList';
import { ProductDiscount } from './component/ProductDiscount';
import I18n from '../../components/Translate/I18n';
import { ProductChangeVirtualComponent } from './component/ProductChangeVirtualComponent';
import oplAppStyle from '../../assets/themes/OPL/jss/App/OplAppStyle';
import FeaturesAwaitingList from '../Features/FeaturesAwaitingList';

interface ProductState {
    product: SimCardInterface | null;
    uri: string;
}

interface ProductProps {
    classes: any;
    dispatch: any;
    match: any;
    showMainLoader(value: boolean): void;
    productOperation?: ProductActionField;
}

class Product extends PureComponent<ProductProps, ProductState> {
    private itemsLoader: ItemLoaders;

    constructor(props: ProductProps, context: ProductState) {
        super(props, context);
        this.state = {
            product: null,
            uri: '/' + props.match.params.id,
        };
        this.itemsLoader = new ItemLoaders(this.props.dispatch);
    }

    componentDidMount(): void {
        const { dispatch } = this.props;
        dispatch({
            type: ProductActions.PRODUCT_GET_ALLOWED_DISCOUNT_LIST,
            discounts: null,
        } as ProductActionField);
        this.loadProduct();
    }

    componentDidUpdate = (prevProps: Readonly<ProductProps>, prevState: Readonly<ProductState>, snapshot?: any): void => {
        const { dispatch, productOperation } = this.props;
        if (!productOperation || !productOperation.type) {
            return;
        }
        if (productOperation.uri && productOperation.type === ProductActions.PRODUCT_REFRESH_VIEW) {
            this.setState({}, () => {
                dispatch({ type: ProductActions.PRODUCT_REFRESH_VIEW, uri: null });
                this.loadProduct();
            });
        }
    };

    returnListCallback = (): void => {
        history.goBack();
    };

    loadProduct = (): void => {
        const { uri } = this.state;
        const { showMainLoader } = this.props;
        showMainLoader(true);
        this.itemsLoader.getWithCallback(
            FormGeneralHelper.getByUri(uri),
            'get_product_item',
            (product: SimCardInterface): void => {
                this.setState({ product: product });
                showMainLoader(false);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false, dispatchErrorAfterCallback: true }
        );
    };

    render(): React.ReactNode {
        const { classes, showMainLoader } = this.props;
        const { product } = this.state;
        if (!product) {
            return <></>;
        }
        return (
            <GridContainer spacing={2}>
                <GridItem xs={12}>
                    <Card>
                        <CardHeader icon={<SimCard />} trans="simcard:edit.view_sim_card" />
                        <CardBody>
                            <SimCardView
                                item={product}
                                classes={classes}
                                callback={this.returnListCallback}
                                refreshPage={this.loadProduct}
                                itemsLoader={this.itemsLoader}
                            />
                        </CardBody>
                    </Card>
                </GridItem>
                {SimCardOperationHelper.isFullyActive(product) && (
                    <>
                        <GridItem xs={8} md={8}>
                            <ProductDiscount platformResellerId={product.client.platformResellerId} simCard={product} />
                        </GridItem>
                        {!product.allowChangeExpiryDateOnComponent && !product.isPrepaid && (
                            <GridItem xs={4} md={4}>
                                <ProductChangeVirtualComponent
                                    platformResellerId={product.client.platformResellerId}
                                    simCard={product}
                                    reload={(): void => {
                                        this.setState({ product: null }, this.loadProduct);
                                    }}
                                />
                            </GridItem>
                        )}
                    </>
                )}
                {!product.deleted && SimCardOperationHelper.isFullyActive(product) && (
                    <GridItem xs={12}>
                        <Card>
                            <CardBody>
                                <ProductOplView product={product} loadProduct={this.loadProduct} />
                            </CardBody>
                        </Card>
                    </GridItem>
                )}
                {SimCardOperationHelper.isFullyActive(product) &&
                    !product.deleted &&
                    (UserRoleHelper.checkRole(roles.ROLE_PLATFORM_SIMCARD_SERVICE_GET) ||
                        UserRoleHelper.checkRole(roles.ROLE_SIM_CARD_SERVICE_TYPE_GET)) &&
                    UserRoleHelper.checkRole(roles.ROLE_PLATFORM_SIMCARD_SERVICE_GET) && (
                        <GridItem xs={12} md={8}>
                            <Card>
                                <CardHeader icon={<FlashOn />} trans="simcard:f.packages" />
                                <CardBody>
                                    <FeaturesActiveList simCard={product} />
                                </CardBody>
                            </Card>
                        </GridItem>
                    )}
                {SimCardOperationHelper.isFullyActive(product) &&
                    !product.deleted &&
                    (UserRoleHelper.checkRole(roles.ROLE_PLATFORM_SIMCARD_SERVICE_GET) ||
                        UserRoleHelper.checkRole(roles.ROLE_SIM_CARD_SERVICE_TYPE_GET)) &&
                    UserRoleHelper.checkRole(roles.ROLE_SIM_CARD_SERVICE_TYPE_GET) &&
                    (SimCardOperationHelper.isMvnoActive(product) || Auth.isExpertMode()) && (
                        <GridItem xs={12} md={4}>
                            <Card>
                                <CardHeader icon={<FlashOn />} trans="simcard:f.components_list" />
                                <CardBody>
                                    <FeaturesNotActiveList simCard={product} />
                                </CardBody>
                            </Card>
                        </GridItem>
                    )}
                {SimCardOperationHelper.isFullyActive(product) &&
                    !product.deleted &&
                    (UserRoleHelper.checkRole(roles.ROLE_PLATFORM_SIMCARD_SERVICE_GET) ||
                        UserRoleHelper.checkRole(roles.ROLE_SIM_CARD_SERVICE_TYPE_GET)) &&
                    UserRoleHelper.checkRole(roles.ROLE_PLATFORM_SIMCARD_SERVICE_GET) &&
                    UserRoleHelper.checkRole(roles.ROLE_PLATFORM_SIMCARD_SERVICE_TYPE_PENDING) &&
                    (
                        <GridItem xs={12} md={8}>
                            <Card>
                                <CardHeader icon={<FlashOn />} trans="simcard:f.components_list_awaiting"  />
                                <CardBody>
                                    <FeaturesAwaitingList simCard={product} />
                                </CardBody>
                            </Card>
                        </GridItem>
                    )}

                {/*{product && (*/}
                {/*    <GridItem xs={12}>*/}
                {/*        <Card>*/}
                {/*            <CardHeader icon={<FlashOn />} trans="simcard:f.fup_history" />*/}
                {/*            <CardBody>*/}
                {/*                <ProductFupHistory simCard={product} showMainLoader={showMainLoader} />*/}
                {/*            </CardBody>*/}
                {/*        </Card>*/}
                {/*    </GridItem>*/}
                {/*)}*/}
                {UserRoleHelper.checkRole(roles.ROLE_MNO_MESSAGE_GET) && (
                    <GridItem xs={12}>
                        <MnoMessageList simCard={product} showMainLoader={showMainLoader} />
                    </GridItem>
                )}
                {!SimCardOperationHelper.isFullyActive(product) && (
                    <GridItem xs={12}>
                        <Card>
                            <CardHeader icon={<FlashOn />} trans="simcard:info.status" />
                            <CardBody>
                                <I18n trans={'simcard:info.product_not_active'} />
                            </CardBody>
                        </Card>
                    </GridItem>
                )}
            </GridContainer>
        );
    }
}

const stateToProps = (state: { productReducers: ProductActionField }): object => {
    const { productReducers } = state;
    if (productReducers) {
        return { productOperation: productReducers };
    }
    return {};
};

export default compose<any, any>(connect(stateToProps), withStyles(oplAppStyle))(Product);
